import { type ReactNode, useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

interface Section {
  id: string;
  label: string;
  icon?: ReactNode;
}

interface LegalPageLayoutProps {
  title: string;
  lastUpdated: string;
  sections: Section[];
  children: ReactNode;
}

export function LegalPageLayout({ title, lastUpdated, sections, children }: LegalPageLayoutProps) {
  const [activeSection, setActiveSection] = useState<string>(sections[0]?.id || '');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      const scrollPosition = target.scrollTop + 100;
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i].id);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };

    const contentArea = document.querySelector('.legal-content-area');
    if (contentArea) {
      contentArea.addEventListener('scroll', handleScroll);
      return () => contentArea.removeEventListener('scroll', handleScroll);
    }

    const handleWindowScroll = () => {
      const scrollPosition = window.scrollY + 100;
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i].id);
        if (section) {
          const sectionTop = section.getBoundingClientRect().top + window.scrollY;
          if (sectionTop <= scrollPosition) {
            setActiveSection(sections[i].id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleWindowScroll);
    return () => window.removeEventListener('scroll', handleWindowScroll);
  }, [sections]);

  const handleSectionClick = (sectionId: string) => {
    setActiveSection(sectionId);
    setMobileMenuOpen(false);
    const element = document.getElementById(sectionId);
    if (!element) return;

    const contentArea = document.querySelector('.legal-content-area');
    if (contentArea) {
      const yOffset = -80;
      contentArea.scrollTo({ top: element.offsetTop + yOffset, behavior: 'smooth' });
    } else {
      const yOffset = -80;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile fixed nav */}
      <div className="md:hidden px-4 py-4 border-b bg-background sticky top-0 z-40 shadow-sm">
        <Button
          variant="outline"
          className="w-full justify-between"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <span className="font-medium">
            {sections.find((s) => s.id === activeSection)?.label || 'Navigate'}
          </span>
          <ChevronDown
            className={cn('w-4 h-4 transition-transform', mobileMenuOpen && 'rotate-180')}
          />
        </Button>

        {mobileMenuOpen && (
          <div className="mt-2 space-y-1 rounded-lg border bg-card p-2 max-h-[60vh] overflow-y-auto">
            {sections.map((section) => (
              <Button
                key={section.id}
                variant={activeSection === section.id ? 'secondary' : 'ghost'}
                onClick={() => handleSectionClick(section.id)}
                className={cn(
                  'w-full justify-start px-3 py-2 text-sm',
                  activeSection === section.id && 'font-medium'
                )}
              >
                {section.label}
              </Button>
            ))}
          </div>
        )}
      </div>

      {/* Desktop layout */}
      <div className="hidden md:flex md:h-screen md:overflow-hidden md:w-full">
        <aside className="w-80 flex-shrink-0 border-r bg-background overflow-y-auto">
          <div className="p-8 space-y-6">
            <h2 className="font-character text-2xl text-foreground">{title}</h2>
            <nav className="space-y-1">
              {sections.map((section) => (
                <Button
                  key={section.id}
                  variant="ghost"
                  onClick={() => handleSectionClick(section.id)}
                  className={cn(
                    'w-full justify-start px-3 py-2 text-sm flex items-center gap-2 hover:bg-secondary',
                    activeSection === section.id && 'font-medium bg-accent text-accent-foreground'
                  )}
                >
                  {section.icon}
                  <span>{section.label}</span>
                </Button>
              ))}
            </nav>
          </div>
        </aside>

        <main className="legal-content-area flex-1 overflow-y-scroll h-full">
          <div className="mx-auto max-w-4xl px-8 py-12">
            <div className="mb-10 text-center">
              <h1 className="text-4xl font-character text-foreground">{title}</h1>
              <p className="text-muted-foreground mt-2">{lastUpdated}</p>
            </div>
            {children}
          </div>
        </main>
      </div>

      {/* Mobile content */}
      <div className="md:hidden px-4 py-8">
        <div className="mb-6 text-center">
          <h1 className="text-3xl font-character text-foreground">{title}</h1>
          <p className="text-muted-foreground mt-2">{lastUpdated}</p>
        </div>
        {children}
      </div>
    </div>
  );
}
